export type HtmlMinifyOptions = {
  /**
   * Collapses runs of whitespace in text nodes and within tags.
   * Default: true
   */
  collapseWhitespace?: boolean;
  /**
   * Removes HTML comments (keeps conditional/SSI comments).
   * Default: true
   */
  removeComments?: boolean;
  /**
   * Removes whitespace-only text nodes between tags.
   * Warning: can change rendering for inline elements.
   * Default: false
   */
  removeIntertagWhitespace?: boolean;
};

const RAW_TEXT_TAGS = new Set(["script", "style", "textarea", "pre", "code"]);

function isAsciiWhitespaceChar(ch: string): boolean {
  return ch === " " || ch === "\n" || ch === "\r" || ch === "\t" || ch === "\f";
}

function looksLikeConditionalOrSsiComment(comment: string): boolean {
  const head = comment.slice(0, 30).toLowerCase();
  if (head.startsWith("<!--#")) return true; // SSI
  if (head.startsWith("<!--[if") || head.startsWith("<!-- [if")) return true;
  if (comment.toLowerCase().includes("<![endif")) return true;
  return false;
}

function readTag(
  html: string,
  start: number
): { tag: string; nextIndex: number } {
  const len = html.length;
  let i = start + 1;
  let quote: "'" | '"' | null = null;

  while (i < len) {
    const c = html[i];
    if (quote) {
      if (c === quote) quote = null;
      i += 1;
      continue;
    }

    if (c === '"' || c === "'") {
      quote = c;
      i += 1;
      continue;
    }

    if (c === ">") {
      i += 1;
      break;
    }
    i += 1;
  }

  return { tag: html.slice(start, i), nextIndex: i };
}

function getTagInfo(tag: string): {
  name: string;
  isClosing: boolean;
  isSelfClosing: boolean;
  isSpecial: boolean;
} | null {
  if (!tag.startsWith("<") || tag.length < 3) return null;

  let i = 1;
  while (i < tag.length && isAsciiWhitespaceChar(tag[i])) i += 1;

  let isClosing = false;
  if (tag[i] === "/") {
    isClosing = true;
    i += 1;
  }

  while (i < tag.length && isAsciiWhitespaceChar(tag[i])) i += 1;
  const first = tag[i];
  if (!first) return null;
  if (first === "!" || first === "?") {
    return { name: "", isClosing, isSelfClosing: false, isSpecial: true };
  }

  let name = "";
  while (i < tag.length) {
    const c = tag[i];
    if (isAsciiWhitespaceChar(c) || c === ">" || c === "/") break;
    name += c.toLowerCase();
    i += 1;
  }

  if (!name) return null;
  const isSelfClosing = !isClosing && /\/\s*>$/.test(tag);
  return { name, isClosing, isSelfClosing, isSpecial: false };
}

function looksLikeClosingTagAt(
  html: string,
  start: number,
  tagName: string
): boolean {
  if (html[start] !== "<") return false;
  let i = start + 1;
  if (html[i] !== "/") return false;
  i += 1;

  while (i < html.length && isAsciiWhitespaceChar(html[i])) i += 1;
  if (i + tagName.length > html.length) return false;

  const candidate = html.slice(i, i + tagName.length).toLowerCase();
  if (candidate !== tagName) return false;

  const next = html[i + tagName.length];
  return !next || isAsciiWhitespaceChar(next) || next === ">" || next === "/";
}

function collapseWhitespaceText(text: string): string {
  let out = "";
  let inWhitespace = false;

  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (isAsciiWhitespaceChar(c)) {
      inWhitespace = true;
      continue;
    }

    if (inWhitespace) {
      out += " ";
      inWhitespace = false;
    }
    out += c;
  }

  return out;
}

function minifyTag(tag: string): string {
  if (tag.length <= 2) return tag;

  let out = "<";
  let i = 1;
  let quote: "'" | '"' | null = null;
  let pendingSpace = false;

  while (i < tag.length - 1) {
    const c = tag[i];

    if (quote) {
      out += c;
      if (c === quote) quote = null;
      i += 1;
      continue;
    }

    if (c === '"' || c === "'") {
      quote = c;
      out += c;
      pendingSpace = false;
      i += 1;
      continue;
    }

    if (isAsciiWhitespaceChar(c)) {
      pendingSpace = true;
      i += 1;
      continue;
    }

    if (c === "=") {
      if (out.endsWith(" ")) out = out.slice(0, -1);
      out += "=";
      pendingSpace = false;
      i += 1;
      while (i < tag.length - 1 && isAsciiWhitespaceChar(tag[i])) i += 1;
      continue;
    }

    if (c === "/") {
      if (out.endsWith(" ")) out = out.slice(0, -1);
      pendingSpace = false;
      out += "/";
      i += 1;
      continue;
    }

    if (pendingSpace) {
      if (
        !out.endsWith("<") &&
        !out.endsWith("</") &&
        !out.endsWith("<!") &&
        !out.endsWith("<?") &&
        !out.endsWith(" ")
      ) {
        out += " ";
      }
      pendingSpace = false;
    }

    out += c;
    i += 1;
  }

  if (out.endsWith(" ")) out = out.slice(0, -1);
  return out + ">";
}

export function minifyHtml(
  input: string,
  options: HtmlMinifyOptions = {}
): string {
  const opts: Required<HtmlMinifyOptions> = {
    collapseWhitespace: options.collapseWhitespace ?? true,
    removeComments: options.removeComments ?? true,
    removeIntertagWhitespace: options.removeIntertagWhitespace ?? false,
  };

  const out: string[] = [];
  const rawTagStack: string[] = [];
  let i = 0;
  let lastOutWasSpace = false;

  while (i < input.length) {
    const ch = input[i];

    if (ch === "<") {
      if (rawTagStack.length > 0) {
        const rawTag = rawTagStack[rawTagStack.length - 1];
        if (looksLikeClosingTagAt(input, i, rawTag)) {
          const { tag, nextIndex } = readTag(input, i);
          out.push(opts.collapseWhitespace ? minifyTag(tag) : tag);
          rawTagStack.pop();
          lastOutWasSpace = false;
          i = nextIndex;
          continue;
        }

        out.push("<");
        lastOutWasSpace = false;
        i += 1;
        continue;
      }

      if (input.startsWith("<!--", i)) {
        const end = input.indexOf("-->", i + 4);
        const comment = end === -1 ? input.slice(i) : input.slice(i, end + 3);
        i = end === -1 ? input.length : end + 3;

        if (opts.removeComments && !looksLikeConditionalOrSsiComment(comment)) {
          continue;
        }

        out.push(opts.collapseWhitespace ? comment.trim() : comment);
        lastOutWasSpace = false;
        continue;
      }

      const { tag, nextIndex } = readTag(input, i);
      const minifiedTag = opts.collapseWhitespace ? minifyTag(tag) : tag;
      out.push(minifiedTag);

      const tagInfo = getTagInfo(tag);
      if (tagInfo && !tagInfo.isSpecial) {
        if (
          !tagInfo.isClosing &&
          !tagInfo.isSelfClosing &&
          RAW_TEXT_TAGS.has(tagInfo.name)
        ) {
          rawTagStack.push(tagInfo.name);
        }
      }

      lastOutWasSpace = false;
      i = nextIndex;
      continue;
    }

    const nextLt = input.indexOf("<", i);
    const end = nextLt === -1 ? input.length : nextLt;
    const text = input.slice(i, end);
    i = end;

    if (rawTagStack.length > 0) {
      out.push(text);
      lastOutWasSpace = false;
      continue;
    }

    if (!opts.collapseWhitespace) {
      out.push(text);
      lastOutWasSpace = text.endsWith(" ");
      continue;
    }

    const collapsed = collapseWhitespaceText(text);
    const hasNonWhitespace = collapsed.trim().length > 0;

    if (!hasNonWhitespace) {
      const atStart = out.length === 0;
      const atEnd = i >= input.length;

      if (
        !opts.removeIntertagWhitespace &&
        !atStart &&
        !atEnd &&
        !lastOutWasSpace
      ) {
        out.push(" ");
        lastOutWasSpace = true;
      }
      continue;
    }

    let normalized = collapsed;
    if (out.length === 0 || lastOutWasSpace)
      normalized = normalized.trimStart();
    if (!normalized) continue;

    out.push(normalized);
    lastOutWasSpace = normalized.endsWith(" ");
  }

  const result = out.join("");
  return opts.collapseWhitespace ? result.trim() : result;
}

export function getUtf8ByteLength(text: string): number {
  if (!text) return 0;
  return new TextEncoder().encode(text).length;
}
